import React, { useState, useEffect } from 'react';
import { api } from '../services/api';

export default function ManualReservation({ onSaved }) {
  const [form, setForm] = useState({ date:'',time_slot:'',party_size:'',table_id:'', customer_name:'',phone:'',email:'', dietary_restrictions:'',special_requests:'',notes:'',status:'confirmed' });
  const [tables, setTables]         = useState([]);
  const [combination, setCombination] = useState(null); // { tables, tableIds, label, totalCapacity, note }
  const [useCombo, setUseCombo]     = useState(false);
  const [checking, setCheck]        = useState(false);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');
  const [success, setSuccess]       = useState('');

  useEffect(() => {
    const {date,time_slot,party_size} = form;
    if (!date||!time_slot||!party_size) { setTables([]); setCombination(null); setUseCombo(false); return; }
    let dead = false;
    setCheck(true);
    api.getAvailability(date,time_slot,parseInt(party_size))
      .then(d=>{
        if(dead) return;
        setTables(d.tables||[]);
        setCombination(d.combination||null);
        // Auto-select combination if no singles available but combo exists
        if ((d.tables||[]).length === 0 && d.combination) setUseCombo(true);
        else setUseCombo(false);
      })
      .catch(()=>{ if(!dead) { setTables([]); setCombination(null); } })
      .finally(()=>{ if(!dead) setCheck(false); });
    return ()=>{ dead=true; };
  }, [form.date,form.time_slot,form.party_size]);

  const set = (k,v) => {
    setError('');
    setForm(f => { const next={...f,[k]:v}; if(['date','time_slot','party_size'].includes(k)) next.table_id=''; return next; });
  };

  const submit = async e => {
    e.preventDefault(); setError(''); setSuccess('');
    if (useCombo && combination) {
      // Combination booking: use primary table + store combined_tables
      const miss = ['date','time_slot','party_size','customer_name'].filter(k=>!form[k]);
      if (!form.phone && !form.email) miss.push('phone or email');
      if (miss.length) { setError('Please fill in: '+miss.join(', ')); return; }
      setSaving(true);
      try {
        const comboNote = combination.note || '';
        const requests = [form.special_requests, comboNote].filter(Boolean).join(' | ');
        const res = await api.createReservation({
          ...form,
          party_size: parseInt(form.party_size),
          table_id: combination.tableIds[0],
          combined_tables: JSON.stringify(combination.tableIds),
          special_requests: requests,
        });
        setSuccess('Reservation created! Confirmation: '+res.confirmation_number);
        setForm({date:'',time_slot:'',party_size:'',table_id:'',customer_name:'',phone:'',email:'',dietary_restrictions:'',special_requests:'',notes:'',status:'confirmed'});
        setTimeout(onSaved,1500);
      } catch(err){ setError(err.message); } finally { setSaving(false); }
      return;
    }
    const miss = ['date','time_slot','party_size','table_id','customer_name'].filter(k=>!form[k]);
    if (!form.phone && !form.email) miss.push('phone or email');
    if (miss.length) { setError('Please fill in: '+miss.join(', ')); return; }
    setSaving(true);
    try {
      const res = await api.createReservation({...form,party_size:parseInt(form.party_size)});
      setSuccess('Reservation created! Confirmation: '+res.confirmation_number);
      setForm({date:'',time_slot:'',party_size:'',table_id:'',customer_name:'',phone:'',email:'',dietary_restrictions:'',special_requests:'',notes:'',status:'confirmed'});
      setTimeout(onSaved,1500);
    } catch(err){ setError(err.message); } finally { setSaving(false); }
  };

  const slotReady = form.date && form.time_slot && form.party_size;

  return (
    <div>
      <h1 style={s.h1}>Add Booking</h1>
      <p style={s.sub}>Manually enter a reservation. It will immediately sync to the AI and other staff.</p>
      <form onSubmit={submit} style={s.form}>
        {error   && <div style={s.error}>{error}</div>}
        {success && <div style={s.ok}>{success}</div>}

        <div style={s.card}>
          <h2 style={s.cardTitle}>Booking slot</h2>
          <div style={s.row}>
            <Field label="Date *"><input style={s.input} className="glass-input" type="date" value={form.date} onChange={e=>set('date',e.target.value)} required /></Field>
            <Field label="Time *"><input style={s.input} className="glass-input" type="time" value={form.time_slot} onChange={e=>set('time_slot',e.target.value)} required /></Field>
            <Field label="Party size *"><input style={s.input} className="glass-input" type="number" min="1" max="50" value={form.party_size} onChange={e=>set('party_size',e.target.value)} required /></Field>
          </div>

          {/* Table / combination selector */}
          <Field label="Table *">
            {checking ? (
              <div style={s.hint}>Checking availability…</div>
            ) : !slotReady ? (
              <div style={s.hint}>Fill in date, time and party size first</div>
            ) : tables.length === 0 && !combination ? (
              <div style={s.noTables}>No tables available for this slot</div>
            ) : (
              <>
                {/* Combination option */}
                {combination && (
                  <div
                    style={{ ...s.comboCard, ...(useCombo ? s.comboCardActive : {}) }}
                    onClick={() => { setUseCombo(true); set('table_id', ''); }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ ...s.comboRadio, ...(useCombo ? s.comboRadioActive : {}) }} />
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
                          {combination.label} (combined)
                        </div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>
                          {combination.totalCapacity} seats total · Push tables together
                        </div>
                      </div>
                      <span style={{ marginLeft: 'auto', background: '#f0f4ff', color: '#4f46e5', fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 6 }}>BEST FIT</span>
                    </div>
                  </div>
                )}

                {/* Single tables */}
                {tables.length > 0 && (
                  <select
                    style={{ ...s.input, marginTop: combination ? 8 : 0 }}
                    className="glass-input"
                    value={useCombo ? '' : form.table_id}
                    onChange={e => { setUseCombo(false); set('table_id', e.target.value); }}
                  >
                    <option value="">{combination ? 'Or choose a single table' : 'Select a table'}</option>
                    {tables.map(t=><option key={t.id} value={t.id}>{t.label} — {t.capacity} seats ({t.location})</option>)}
                  </select>
                )}
              </>
            )}
          </Field>

          <Field label="Status">
            <select style={s.input} className="glass-input" value={form.status} onChange={e=>set('status',e.target.value)}>
              <option value="confirmed">Confirmed</option><option value="pending">Pending</option>
            </select>
          </Field>
        </div>

        <div style={s.card}>
          <h2 style={s.cardTitle}>Customer details</h2>
          <div style={s.row}>
            <Field label="Full name *"><input style={s.input} className="glass-input" type="text" value={form.customer_name} onChange={e=>set('customer_name',e.target.value)} required /></Field>
            <Field label="Phone"><input style={s.input} className="glass-input" type="tel" value={form.phone} onChange={e=>set('phone',e.target.value)} /></Field>
            <Field label="Email"><input style={s.input} className="glass-input" type="email" value={form.email} onChange={e=>set('email',e.target.value)} /></Field>
          </div>
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: -8, marginBottom: 4 }}>Phone or email required</div>
        </div>

        <div style={s.card}>
          <h2 style={s.cardTitle}>Additional info</h2>
          <Field label="Dietary restrictions"><input style={s.input} className="glass-input" type="text" placeholder="e.g. vegetarian, nut allergy" value={form.dietary_restrictions} onChange={e=>set('dietary_restrictions',e.target.value)} /></Field>
          <Field label="Special requests"><input style={s.input} className="glass-input" type="text" placeholder="e.g. birthday, high chair" value={form.special_requests} onChange={e=>set('special_requests',e.target.value)} /></Field>
          <Field label="Staff notes"><textarea style={{...s.input,minHeight:72,resize:'vertical'}} className="glass-input" value={form.notes} onChange={e=>set('notes',e.target.value)} /></Field>
        </div>

        <button type="submit" style={s.submit} className="glass-btn t" disabled={saving}>
          {saving ? 'Creating…' : 'Create Reservation →'}
        </button>
      </form>
    </div>
  );
}

function Field({label,children}) {
  return (
    <div style={{display:'flex',flexDirection:'column',gap:6,flex:1,minWidth:180,marginBottom:16}}>
      <label style={{fontSize:11,fontWeight:700,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.06em'}}>{label}</label>
      {children}
    </div>
  );
}

const s = {
  h1:       { fontSize:28, fontWeight:800, color:'var(--text-primary)', margin:0, letterSpacing:'-0.5px' },
  sub:      { fontSize:14, color:'var(--text-muted)', marginTop:4, marginBottom:26 },
  form:     { maxWidth:760 },
  card:     { background:'#ffffff', border:'1px solid #e2e8f0', borderRadius:20, padding:'22px 26px', marginBottom:14, boxShadow:'0 1px 4px rgba(15,23,42,0.06)' },
  cardTitle:{ fontSize:11, fontWeight:700, color:'var(--text-muted)', marginBottom:18, marginTop:0, textTransform:'uppercase', letterSpacing:'0.08em' },
  row:      { display:'flex', gap:16, flexWrap:'wrap' },
  input:    { background:'var(--input-bg)', border:'1px solid var(--input-border)', borderRadius:10, padding:'10px 13px', fontSize:14, outline:'none', width:'100%', boxSizing:'border-box', color:'var(--input-text)', transition:'all 0.18s' },
  hint:     { fontSize:13, color:'var(--text-muted)', padding:'10px 0' },
  noTables: { fontSize:13, color:'#dc2626', padding:'10px 0' },
  error:    { background:'#fef2f2', color:'#dc2626', border:'1px solid #fecaca', borderRadius:10, padding:'12px 16px', fontSize:13, marginBottom:16 },
  ok:       { background:'#f0fdf4', color:'#15803d', border:'1px solid #bbf7d0', borderRadius:10, padding:'12px 16px', fontSize:13, marginBottom:16 },
  submit:   { background:'var(--accent)', border:'none', borderRadius:12, padding:'14px 32px', fontSize:15, fontWeight:700, cursor:'pointer', color:'#fff' },

  comboCard:      { border:'2px solid #e2e8f0', borderRadius:12, padding:'14px 16px', cursor:'pointer', transition:'border-color 0.15s, background 0.15s', background:'#fff', marginBottom: 0 },
  comboCardActive:{ borderColor:'#4f46e5', background:'#f0f4ff' },
  comboRadio:     { width:16, height:16, borderRadius:'50%', border:'2px solid #cbd5e1', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', transition:'border-color 0.15s' },
  comboRadioActive:{ border:'5px solid #4f46e5' },
};
